import * as Sentry from "@sentry/react";
import { search, sortKind } from "fast-fuzzy";
import React, { useRef, useState } from "react";
import { TemplateIcon } from "../templates/TemplateIcon.js";
import {
  HeaderInputOption,
  RECOMMENDATIONS_HEADER_OPTION_ID,
  RecommendationInputOption,
} from "./createItemInputOptions.js";

/**
 * Neutral shape a consumer must return from `fetchItemRecommendations`.
 * Keeps GraphQL-specific types out of the generic CreateItemInput component.
 *
 * Only template recommendations are surfaced in the combobox (template-type
 * recommendations are filtered out by the consumer), so the exact template id
 * and version are carried through for the created item.
 */
export type CreateItemRecommendation = {
  id: string;
  teaserTitle: string;
  rank: number;
  templateId: string;
  templateVersionNumber: number;
};

/**
 * Builds the "Recommended templates" section (header + rows). `items` is the
 * full recommendation list in the empty-search state, or the fuzzy-matched
 * subset while the user is typing. Returns [] when there is nothing to show
 * (loaded with no items), so the header only appears when relevant.
 */
export function buildRecommendationOptions({
  items,
  allowRecommendations,
  recommendationsLoading,
  headerTitle,
  rowInputValue,
}: {
  items: CreateItemRecommendation[];
  allowRecommendations: boolean;
  recommendationsLoading: boolean;
  headerTitle: string;
  rowInputValue: string;
}): Array<HeaderInputOption | RecommendationInputOption> {
  if (!allowRecommendations) return [];
  if (!recommendationsLoading && items.length === 0) return [];

  const header: HeaderInputOption = {
    id: RECOMMENDATIONS_HEADER_OPTION_ID,
    isHeader: true as const,
    title: headerTitle,
    isLoading: recommendationsLoading,
  };

  // No rows under the spinner: a recommendation must never be selectable
  // while the list it belongs to is being (re)fetched.
  const rows: RecommendationInputOption[] = recommendationsLoading
    ? []
    : [...items]
        .sort((a, b) => a.rank - b.rank)
        .map(recommendation => ({
          id: `recommendation-${recommendation.id}`,
          itemRecommendationId: recommendation.id,
          title: recommendation.teaserTitle,
          templateId: recommendation.templateId,
          templateVersionNumber: recommendation.templateVersionNumber,
          isRecommendation: true as const,
          icon: <TemplateIcon style={{ flexGrow: 0, flexShrink: 0 }} />,
          inputValue: rowInputValue,
        }));

  return [header, ...rows];
}

/**
 * Fuzzy-matches the typed term against recommendation teaser titles so
 * matching recommended templates appear in their own section while the user
 * is searching.
 */
export function matchRecommendations(
  inputValue: string,
  recommendations: CreateItemRecommendation[]
): CreateItemRecommendation[] {
  return search(inputValue, recommendations, {
    keySelector: recommendation => recommendation.teaserTitle,
    threshold: 0.6,
    sortBy: sortKind.insertOrder,
  });
}

export function useCreateItemAutoCompleteRecommendations({
  itemId,
  enabled,
  fetchItemRecommendations,
}: {
  itemId: string;
  enabled: boolean;
  fetchItemRecommendations:
    | ((itemId: string) => Promise<CreateItemRecommendation[]>)
    | undefined;
}) {
  // Loaded state carries the item it was fetched for. Virtualized rows reuse
  // component instances, so on a changed itemId a mismatched tag reads as
  // "nothing loaded" — the previous item's rows can neither be rendered nor
  // selected while the new item has not fetched yet.
  const [loaded, setLoaded] = useState<{
    itemId: string;
    recommendations: CreateItemRecommendation[];
    loading: boolean;
  }>(() => ({ itemId, recommendations: [], loading: false }));
  // The item a fetch is currently running for: blocks a duplicate concurrent
  // fetch for the same item without blocking the next item's fetch, and lets a
  // resolved fetch tell whether it is still the request being awaited.
  const inFlightItemIdRef = useRef<string | undefined>(undefined);

  const loadRecommendations = React.useCallback(async () => {
    if (!enabled || !fetchItemRecommendations) return;
    if (inFlightItemIdRef.current === itemId) return;
    const requestItemId = itemId;
    inFlightItemIdRef.current = requestItemId;
    setLoaded({ itemId: requestItemId, recommendations: [], loading: true });
    const isCurrent = () => inFlightItemIdRef.current === requestItemId;
    try {
      const fetched = await fetchItemRecommendations(requestItemId);
      if (isCurrent()) {
        setLoaded({
          itemId: requestItemId,
          recommendations: fetched,
          loading: false,
        });
      }
    } catch (error) {
      // On failure clear recommendations so the dropdown never shows stale data.
      Sentry.captureException(error);
      if (isCurrent()) {
        setLoaded({
          itemId: requestItemId,
          recommendations: [],
          loading: false,
        });
      }
    } finally {
      if (isCurrent()) inFlightItemIdRef.current = undefined;
    }
  }, [enabled, fetchItemRecommendations, itemId]);

  const isLoadedForItem = loaded.itemId === itemId;

  return {
    recommendations: isLoadedForItem ? loaded.recommendations : [],
    recommendationsLoading: isLoadedForItem && loaded.loading,
    loadRecommendations,
  };
}
